import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Col, Form, Row } from "antd";
import { useAppDispatch, useAppSelector } from "../../index";
import {
  createContactIndividual,
  deleteIndividual,
  fetchIndividualVersions,
  getAccountById,
} from "../../store/slices/accountsTs";
import { checkIsLoaded, checkIsLoading, useActiveModal } from "../../lib/utils";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { toDisplayFormat, toDisplayFormatWithTime } from "../../lib/utils/formatDate";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import { ContactIndividualModal } from "../AccountCreate/ContactIndividual/ContactIndividualModal";
import plusIcon from "../../img/whitePlus.svg";
import { fetchCountries, fetchRegions } from "../../store/slices/dicts";
import ChangeVersionsTable from "../common/ChangeVersionsTable";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import ModalWindow from "../ModalWindow/ModalWindow";

export const AccountContacts = () => {
  const { activeModal, setActiveModal, activeModalOptions } = useActiveModal("");
  const { accountId } = useParams<{ accountId: string }>();
  const history = useHistory();
  const {
    accountByIdData,
    getAccountByIdLoading,
    individualVersions,
    individualVersionsLoading,
  } = useAppSelector((state) => state.accountsTs);
  const [tableData, setTableData] = useState([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  const [versionsTableForm] = Form.useForm();
  const dispatch = useAppDispatch();

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    firstName: "15%",
    middleName: "15%",
    lastName: "15%",
    position: "20%",
    period: "20%",
    buttons: "7%",
  };

  useEffect(() => {
    dispatch(
      getAccountById({
        accountId,
      })
    );
    dispatch(fetchRegions());
    dispatch(fetchCountries());
  }, []);

  useEffect(() => {
    if (checkIsLoaded(individualVersionsLoading)) {
      setChildTableData(
        individualVersions.map((item) => ({
          key: `${item.id}${item.fromDate}`,
          id: item.id,
          firstName: item.firstName,
          middleName: item.middleName,
          lastName: item.lastName,
          position: item.properties,
          period: item,
          fromDateForDelete: item.fromDate,
        }))
      );
    }
  }, [individualVersions]);

  useEffect(() => {
    if (checkIsLoaded(getAccountByIdLoading)) {
      setTableData(
        accountByIdData?.organization?.contactIndividual.map((item) => ({
          key: `${item.id}${item.fromDate}`,
          id: item.id,
          firstName: item.firstName,
          middleName: item.middleName,
          lastName: item.lastName,
          position: item.properties,
          period: item,
        }))
      );
    }
  }, [accountByIdData]);

  const showHistoryModal = (rec) => {
    setActiveModal("historyAndPropertyModal", rec.id);
  };

  const handleHideModal = () => {
    setActiveModal("", undefined);
  };

  const handleDelete = (row) => {
    const data = { individualId: row.id, fromDate: row.fromDateForDelete };
    dispatch(deleteIndividual(data)).then(() => {
      dispatch(
        getAccountById({
          accountId,
        })
      );
    });
    setActiveModal("");
  };

  const [columns, setColumns] = useState([
    {
      title: "Фамилия",
      dataIndex: "lastName",
      key: "lastName",
      width: columnsWidths.lastName,
      hidden: false,
    },
    {
      title: "Имя",
      dataIndex: "firstName",
      key: "firstName",
      width: columnsWidths.firstName,
      hidden: false,
    },
    {
      title: "Отчество",
      dataIndex: "middleName",
      key: "middleName",
      width: columnsWidths.middleName,
      hidden: false,
    },
    {
      title: "Должность",
      dataIndex: "position",
      key: "position",
      width: columnsWidths.position,
      hidden: false,
      render: (record) => {
        const positionObject = record?.find((item) => item?.type?.id === "position");
        return positionObject?.stringValue;
      },
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (record) => {
        return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      render: (value, record) => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal(record);
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        </div>
      ),
    },
  ]);

  const [childTableColumns, setChildTableColumns] = useState([
    {
      title: "Фамилия",
      dataIndex: "lastName",
      key: "lastName",
      width: columnsWidths.lastName,
      hidden: false,
    },
    {
      title: "Имя",
      dataIndex: "firstName",
      key: "firstName",
      width: columnsWidths.firstName,
      hidden: false,
    },
    {
      title: "Отчество",
      dataIndex: "middleName",
      key: "middleName",
      width: columnsWidths.middleName,
      hidden: false,
    },
    {
      title: "Должность",
      dataIndex: "position",
      key: "position",
      width: columnsWidths.position,
      hidden: false,
      render: (record) => {
        const positionObject =
          record.length > 0 && record?.find((item) => item?.type?.id === "position");
        return positionObject.stringValue;
      },
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (record) => {
        return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
      style: { display: "flex", justifyContent: "end" },
      render: (value, record) => {
        return (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <ChangeVersionsTable.DeleteButton
              record={record}
              onClick={() => setActiveModal("contactIndividualDelete", record)}
              title="Удалить контактное лицо"
            />
            <Button
              onClick={(e) => {
                e.stopPropagation();
                showHistoryModal(record);
              }}
              className="iconButton"
              type="text"
              htmlType="button"
              icon={<DotsIcon />}
            />
          </div>
        );
      },
    },
  ]);
  const handleCreateIndividual = (values) => {
    console.log("values 124---", values);
    const data = {
      organizationId: accountByIdData?.organization?.id,
      body: {
        sex: values?.sex,
        firstName: values?.firstName,
        middleName: values?.middleName,
        lastName: values?.lastName,
        emails: values?.emails.map((item) => {
          return {
            email: item,
          };
        }),
        actualAddress: {
          countryId: values?.countryId,
          regionId: values?.regionId,
          unstructuredAddress: values?.unstructuredAddress,
          postCode: " ",
        },
      },
    };

    if (values?.position) {
      Object.assign(data.body, {
        properties: [
          {
            typeId: "position",
            stringValue: values?.position,
          },
        ],
      });
    }
    if (values?.phones) {
      Object.assign(data.body, {
        phones: values?.phones.map((item) => {
          return {
            phoneTypeId: item?.phoneTypeId,
            phone: item?.phone,
            countryCode: item?.countryCode,
          };
        }),
      });
    }
    dispatch(createContactIndividual(data)).then(({ payload }) => {
      if (payload) {
        dispatch(
          getAccountById({
            accountId,
          })
        );
      }
    });
  };

  return (
    <>
      <div style={{ textAlign: "right", position: "absolute", top: "45px", right: "20px" }}>
        <Button
          type="primary"
          icon={<img src={plusIcon} width="8px" alt="plus" />}
          onClick={() => setActiveModal("contactIndividual")}
        >
          &nbsp;&nbsp;Добавить
        </Button>
      </div>

      <Form form={versionsTableForm} onFinish={handleCreateIndividual}>
        <ToggleableVersionsTable
          tableName="account-list-individual-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          childTableColumns={childTableColumns}
          setChildTableColumns={setChildTableColumns}
          childTableData={childTableData}
          isToggleableColumns={false}
          isTableLoading={checkIsLoading(getAccountByIdLoading)}
          isChildTableLoading={checkIsLoading(individualVersionsLoading)}
          onOpenVersionsCallback={(record, expanded) => {
            if (!expanded) {
              dispatch(fetchIndividualVersions({ individualId: record?.id }));
            }
          }}
          /*  isPageable={{
            current: accountsIndividual.number + 1,
            total: accountsIndividual.totalElements,
            showSizeChanger: false,
            onChange: (page: number) => {
              handlePageableSearch({ ...individualSearchValues, page: page - 1 });
            },
          }} */
          onRow={(record) => ({
            onClick: () => {
              history.push(`/accounts/${accountId}/info/contacts-id?=${record.id}`);
            },
          })}
        />
      </Form>
      <ContactIndividualModal
        visible={activeModal === "contactIndividual"}
        setActiveModal={setActiveModal}
        onSubmit={handleCreateIndividual}
        // contactIndividualFormData={contactIndividualFormData}
      />

      <HistoryAndPropertyModal
        objectTypeSysname="individual"
        objectId={Number(activeModalOptions)}
        handleHideModal={handleHideModal}
        visibleModal={activeModal === "historyAndPropertyModal"}
        // hasAccessToEdit={}
      />

      <ModalWindow
        visible={activeModal === "contactIndividualDelete"}
        handleSubmit={() => handleDelete(activeModalOptions)}
        handleCancel={() => setActiveModal("")}
        handleReset={() => setActiveModal("")}
        cancelTextButton="Отмена"
        submitTextButton="Удалить"
        disabled={false}
      >
        <Row align="middle">
          <Col style={{ textAlign: "center", fontSize: "14px", lineHeight: 1.4 }}>
            Вы действительно хотите удалить контактное лицо?
          </Col>
        </Row>
      </ModalWindow>
    </>
  );
};
